/**
 * Página de histórico de acessos (logins).
 *
 * Responde: "quem entrou no AURA, quando, de qual IP e dispositivo?"
 *
 * Lê autenticacao.sessoes desta instância. Como cada operadora roda seu próprio
 * banco (satélite), só aparecem os usuários desta operadora — não há como vazar
 * login de outro cliente.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Clock, RefreshCw, Search, Monitor } from 'lucide-react';
import toast from 'react-hot-toast';
import { authAPI } from '../services/api';

// yyyy-mm-dd de N dias atrás / hoje, pro <input type="date">
const isoDia = (offsetDias = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDias);
  return d.toISOString().slice(0, 10);
};

const fmtDataHora = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(v);
  }
};

// User-agent cru é ilegível. Extrai um resumo "Navegador · SO".
const resumoDispositivo = (ua) => {
  if (!ua) return '—';
  const nav =
    /Edg\//.test(ua) ? 'Edge' :
    /OPR\/|Opera/.test(ua) ? 'Opera' :
    /Chrome\//.test(ua) ? 'Chrome' :
    /Firefox\//.test(ua) ? 'Firefox' :
    /Safari\//.test(ua) ? 'Safari' : 'Outro';
  const so =
    /Windows/.test(ua) ? 'Windows' :
    /Android/.test(ua) ? 'Android' :
    /iPhone|iPad|iOS/.test(ua) ? 'iOS' :
    /Mac OS X|Macintosh/.test(ua) ? 'macOS' :
    /Linux/.test(ua) ? 'Linux' : '';
  return so ? `${nav} · ${so}` : nav;
};

const AdminHistoricoAcessos = () => {
  const [sessoes, setSessoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inicio, setInicio] = useState(isoDia(-30));
  const [fim, setFim] = useState(isoDia(0));
  const [busca, setBusca] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limite: 1000 };
      if (inicio) params.inicio = `${inicio}T00:00:00`;
      if (fim) params.fim = `${fim}T23:59:59`;
      const { data } = await authAPI.sessoesHistorico(params);
      setSessoes(data || []);
    } catch (err) {
      toast.error('Erro ao carregar histórico de acessos');
    }
    setLoading(false);
  }, [inicio, fim]);

  useEffect(() => { carregar(); }, [carregar]);

  const termo = busca.trim().toLowerCase();
  const filtradas = termo
    ? sessoes.filter((s) => {
        const u = s.usuario || {};
        return (
          (u.nome_completo || '').toLowerCase().includes(termo) ||
          (u.email || '').toLowerCase().includes(termo) ||
          (s.ip_origem || '').toLowerCase().includes(termo)
        );
      })
    : sessoes;

  const th = { textAlign: 'left', padding: '0.6rem 0.75rem', fontSize: '0.8rem', color: 'var(--gray-600)', borderBottom: '2px solid var(--gray-200)', whiteSpace: 'nowrap' };
  const td = { padding: '0.6rem 0.75rem', fontSize: '0.85rem', borderBottom: '1px solid var(--gray-100)', verticalAlign: 'top' };

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.5rem', margin: 0 }}>
            <Clock size={24} />
            Histórico de Acessos
          </h1>
          <p style={{ color: 'var(--gray-600)', marginTop: '0.25rem', fontSize: '0.9rem' }}>
            {filtradas.length} login(s) no período · logins bem-sucedidos desta operadora
          </p>
        </div>
        <button className="btn btn-secondary" onClick={carregar} disabled={loading}>
          <RefreshCw size={16} /> {loading ? 'Carregando...' : 'Atualizar'}
        </button>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: '8px' }}>
        <label style={{ fontSize: '0.8rem', color: 'var(--gray-600)' }}>
          De<br />
          <input type="date" value={inicio} max={fim} onChange={(e) => setInicio(e.target.value)} className="form-control" style={{ marginTop: '0.25rem' }} />
        </label>
        <label style={{ fontSize: '0.8rem', color: 'var(--gray-600)' }}>
          Até<br />
          <input type="date" value={fim} min={inicio} max={isoDia(0)} onChange={(e) => setFim(e.target.value)} className="form-control" style={{ marginTop: '0.25rem' }} />
        </label>
        <label style={{ fontSize: '0.8rem', color: 'var(--gray-600)', flex: 1, minWidth: '200px' }}>
          Buscar (nome, e-mail ou IP)<br />
          <div style={{ position: 'relative', marginTop: '0.25rem' }}>
            <Search size={16} style={{ position: 'absolute', left: '0.5rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
            <input type="text" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Filtrar…" className="form-control" style={{ paddingLeft: '2rem', width: '100%' }} />
          </div>
        </label>
      </div>

      {/* Tabela */}
      <div style={{ overflowX: 'auto', border: '1px solid var(--gray-200)', borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
          <thead>
            <tr>
              <th style={th}>Usuário</th>
              <th style={th}>Login</th>
              <th style={th}>Logout</th>
              <th style={th}>Último acesso</th>
              <th style={th}>IP</th>
              <th style={th}><Monitor size={14} style={{ verticalAlign: 'middle' }} /> Dispositivo</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td style={td} colSpan={6}>Carregando…</td></tr>
            ) : filtradas.length === 0 ? (
              <tr><td style={{ ...td, color: 'var(--gray-500)' }} colSpan={6}>Nenhum acesso no período.</td></tr>
            ) : (
              filtradas.map((s) => {
                const u = s.usuario || {};
                const online = !s.logout_em;
                return (
                  <tr key={s.id}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{u.nome_completo || `#${s.usuario_id}`}</div>
                      <div style={{ color: 'var(--gray-500)', fontSize: '0.78rem' }}>{u.email || ''}</div>
                    </td>
                    <td style={td}>{fmtDataHora(s.login_em)}</td>
                    <td style={td}>
                      {online
                        ? <span style={{ color: 'var(--success, #27ae60)', fontWeight: 600 }}>Sessão ativa</span>
                        : fmtDataHora(s.logout_em)}
                    </td>
                    <td style={td}>{fmtDataHora(s.ultimo_acesso)}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.8rem' }}>{s.ip_origem || '—'}</td>
                    <td style={td} title={s.user_agent || ''}>{resumoDispositivo(s.user_agent)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <p style={{ color: 'var(--gray-500)', fontSize: '0.78rem', marginTop: '0.75rem' }}>
        Só logins bem-sucedidos são registrados. Tentativas com senha errada ainda não são gravadas.
      </p>
    </div>
  );
};

export default AdminHistoricoAcessos;
